import { Graphics } from 'pixi.js';
import { TILE_HALF } from '../sim/projection';
import { DIAMOND, TilePinnedOverlay, type Tile, type Transient } from './tilePinnedOverlay';

/**
 * The on-screen feedback layer for touch **tap-to-walk** (issue #163, PRD #159):
 * the presentation half of the interaction whose routing brain is the pure
 * `input/autowalk` module. It draws three things, all as the 2:1 dimetric tile
 * diamond via the shared projection so they sit squarely on a ground tile:
 *
 * - an expanding **ping** at the resolved destination the instant a tap commits,
 *   for tactile "got it" confirmation - it self-dismisses ({@link pingDestination});
 * - a persistent **destination marker** on the resolved tile that stays put while
 *   the character walks and is removed on arrival / cancel / retarget
 *   ({@link markDestination} / {@link clearDestination}); and
 * - a "**can't get there**" signal - a red ping with an X - when a tap resolves to
 *   no reachable target, or an in-progress walk gives up unable to reach its goal
 *   (re-path cap / no route, #164) ({@link signalBlocked}).
 *
 * It is a thin presentation overlay, not a decision-maker: the installer calls
 * these methods off the same tap-commit / arrival / cancel events the tracer
 * already produces, so this never touches `resolveTarget`/`findPath`/`nextStep`.
 * There is deliberately **no breadcrumb / route line** - it would strobe on every
 * re-path (PRD #159, explicitly out of scope).
 *
 * A {@link TilePinnedOverlay} (Chassis Slice H): the base owns the camera-riding
 * overlay contract, the tile projection through the z-aware `landZ` seam (wired
 * by the renderer on heighted maps, PRD #536), and the transient aging; this
 * subclass owns the marker + ping/blocked drawings. {@link GameRenderer} owns one
 * instance, ages it each frame from the render clock via {@link update}, and
 * exposes the three triggers as pass-throughs. See
 * `docs/adr/0091-tap-to-walk-indicator-overlay.md`.
 */

/** Warm UO gold (the splash/HUD accent) for the confirmation ping + destination marker. */
const CONFIRM_COLOR = 0xd8c690;
/** The app's red-orange (`#e0795a`, used for splash errors) for the can't-get-there signal. */
const BLOCKED_COLOR = 0xe0795a;

/** How long a confirmation ping takes to expand and fade to nothing. */
const PING_TTL_MS = 480;
/** How far a ping's diamond grows from its tile-sized start as it fades. */
const PING_MAX_SCALE = 2.4;
/** The blocked signal lingers a touch longer than a ping so the "no" reads clearly. */
const BLOCKED_TTL_MS = 750;

/** A subtle breathing pulse on the persistent marker so it stays noticeable without distracting. */
const MARKER_PULSE_PERIOD_MS = 1400;
const MARKER_PULSE_AMPLITUDE = 0.08;

/** A ping or the blocked X: expanding-ping animation when `expands`, a steady fade when not. */
interface AutowalkTransient extends Transient {
  expands: boolean;
}

/** Draw the tile-diamond outline + soft fill the destination marker and pings share. */
function diamond(color: number, fillAlpha: number): Graphics {
  const g = new Graphics();
  g.poly(DIAMOND).fill({ color, alpha: fillAlpha }).stroke({ width: 2, color, alpha: 1 });
  return g;
}

export class AutowalkOverlay extends TilePinnedOverlay<AutowalkTransient> {
  /** The persistent destination marker; positioned on a tile and toggled visible. */
  private readonly marker: Graphics;
  /** The tile the marker currently sits on, or `undefined` when it is hidden. */
  private markerOn?: Tile;
  /** Accumulated render time driving the marker's breathing pulse. */
  private pulseMs = 0;

  constructor() {
    super();
    this.marker = diamond(CONFIRM_COLOR, 0.18);
    this.marker.visible = false;
    this.addChild(this.marker);
  }

  /** Show the persistent destination marker on the resolved tile (moves it if already shown). */
  markDestination(tile: Tile): void {
    this.markerOn = { x: tile.x, y: tile.y };
    const c = this.centre(tile);
    this.marker.position.set(c.x, c.y);
    this.marker.visible = true;
  }

  /** Remove the persistent marker (the character arrived, or the walk was cancelled). */
  clearDestination(): void {
    this.markerOn = undefined;
    this.marker.visible = false;
  }

  /** Play one expanding confirmation ping on the resolved tile; it self-dismisses. */
  pingDestination(tile: Tile): void {
    this.spawnPing(tile, CONFIRM_COLOR, PING_TTL_MS);
  }

  /**
   * Show the "can't get there" signal when a destination is unreachable - a tap
   * that resolved to no reachable target, or an in-progress walk that gave up
   * (re-path cap / no route, #164): a red ping plus a red X that fade together.
   * Leaves the persistent marker untouched - the caller clears it as part of the
   * same cancel.
   */
  signalBlocked(tile: Tile): void {
    this.spawnPing(tile, BLOCKED_COLOR, BLOCKED_TTL_MS);
    const c = this.centre(tile);
    const x = new Graphics();
    const r = TILE_HALF * 0.5;
    x.moveTo(-r, -r).lineTo(r, r).moveTo(r, -r).lineTo(-r, r).stroke({ width: 3, color: BLOCKED_COLOR });
    x.position.set(c.x, c.y);
    this.addChild(x);
    this.addTransient({ gfx: x, ageMs: 0, ttlMs: BLOCKED_TTL_MS, expands: false });
  }

  /** True while the persistent destination marker is showing (for the renderer tests). */
  get markerShown(): boolean {
    return this.marker.visible;
  }

  /** The tile the marker sits on, or `undefined` when hidden (for the renderer tests). */
  get markerTile(): Tile | undefined {
    return this.markerOn ? { ...this.markerOn } : undefined;
  }

  /** How many ping/blocked graphics are currently animating (for the renderer tests). */
  get transientCount(): number {
    return this.transients.length;
  }

  /** Whether the marker or a transient is currently changing on the render clock. */
  get requiresActivePresentation(): boolean {
    return this.marker.visible || this.transients.length > 0;
  }

  private spawnPing(tile: Tile, color: number, ttlMs: number): void {
    const c = this.centre(tile);
    const g = diamond(color, 0.12);
    g.position.set(c.x, c.y);
    this.addChild(g);
    this.addTransient({ gfx: g, ageMs: 0, ttlMs, expands: true });
  }

  /**
   * Advance every animation by `dtMs` (the render frame delta): grow + fade the
   * pings, breathe the marker, and destroy any transient that has lived out its
   * TTL. A static refresh (`dtMs <= 0`, e.g. a headless test frame) holds steady.
   */
  update(dtMs: number): void {
    if (dtMs <= 0) return;

    if (this.marker.visible) {
      this.pulseMs += dtMs;
      const phase = (this.pulseMs / MARKER_PULSE_PERIOD_MS) * Math.PI * 2;
      this.marker.scale.set(1 + Math.sin(phase) * MARKER_PULSE_AMPLITUDE);
    }

    this.ageTransients(dtMs, (t, k) => {
      t.gfx.alpha = 1 - k; // fade out over the lifetime
      if (t.expands) {
        // Ease-out growth from a tile-sized start so the ping bursts then settles.
        const ease = 1 - (1 - k) * (1 - k);
        t.gfx.scale.set(1 + ease * (PING_MAX_SCALE - 1));
      }
    });
  }
}
